import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Kalendar } from './Kalendar'
import { danas, razloziDatum, uPrikaz } from './kalendar'
import { OkvirTona, razredPolja, razredPrigusen, type Ton } from './ton'

/**
 * POLJE DATUMA — vlastito, jer nativno o obliku ne pregovara (D15-A §4).
 *
 * Vrijednost je i dalje `yyyy-MM-dd`, ista koju je nosio `<input type="date">`;
 * mijenja se samo ono što korisnik VIDI i način na koji bira. Zašto uopće —
 * `components/datum/kalendar.ts`.
 *
 * **Datum se ne tipka nego bira**, i to je nastavak zakona forme
 * (`components/zakonForme.ts`): datum upisan u obliku koji polje ne razumije bio
 * je nalaz zbog kojeg je najava jednom nestala s ekrana bez ijedne riječi. Ovdje
 * takav unos nije moguć — polje je gumb, a ne tekst.
 *
 * **Obaveznost čuva skrivena kontrola.** `provjeriObrazac` čita `validity` svake
 * kontrole u obrascu, a gumb se ne provjerava; zato uz njega stoji polje koje
 * nosi `required` i vrijednost. Ono je `sr-only` (odrezano, NE `display:none`),
 * jer skriveno polje preglednik ne bi validirao — a upravo mu je to posao — i
 * nosi `data-fokus`, pa fokus nakon pale provjere ide na GUMB koji se vidi.
 */
export function PoljeDatuma({
  ton = 'konzola',
  label,
  hint,
  greska,
  obavezno,
  krupno = false,
  value,
  onValue,
  min,
  max,
  name,
  ...ostalo
}: {
  ton?: Ton
  label: string
  hint?: string
  greska?: string
  obavezno?: boolean
  /** Polje kroz koje se BIRA nosi veću metu i veće slovo (§13). */
  krupno?: boolean
  /** `yyyy-MM-dd`; prazno = nije izabrano. */
  value: string
  onValue: (vrijednost: string) => void
  min?: string
  max?: string
  name?: string
  /** `data-*` s ekrana (testovi i dalje traže polje po njima). */
  [ostalo: string]: unknown
}) {
  const { t } = useTranslation()

  const [otvoren, postaviOtvoren] = useState(false)

  // Koji se mjesec gleda. Sidro je izabrani dan, pa `min`, pa danas — pult koji
  // otvara kalendar nad praznim poljem s granicom "od danas" ne smije završiti u
  // mjesecu u kojem ne može ništa izabrati.
  const sidro = razloziDatum(value) ?? razloziDatum(min) ?? razloziDatum(danas())!
  const [mjesec, postaviMjesec] = useState({ godina: sidro.godina, mjesec: sidro.mjesec })

  return (
    <OkvirTona
      ton={ton}
      label={label}
      hint={hint}
      greska={greska}
      obavezno={obavezno}
      krupno={krupno}
    >
      {(id, opisano) => (
        <div>
          <button
            id={id}
            type="button"
            aria-describedby={opisano}
            aria-invalid={greska ? true : undefined}
            aria-expanded={otvoren}
            data-picker="datum"
            onClick={() => {
              // Mjesec se poravnava pri OTVARANJU, a ne pri svakom crtanju: pult
              // koji je odlistao do prosinca pa zatvorio i opet otvorio traži
              // izabrani dan, ne prosinac.
              if (!otvoren) postaviMjesec({ godina: sidro.godina, mjesec: sidro.mjesec })

              postaviOtvoren((prije) => !prije)
            }}
            className={`${razredPolja(ton, krupno, greska)} flex items-center justify-between text-left`}
            {...ostalo}
          >
            <span className={value === '' ? razredPrigusen(ton) : undefined}>
              {value === '' ? t('polje.datum.prazno') : uPrikaz(value)}
            </span>
            <span aria-hidden="true" className={razredPrigusen(ton)}>
              ▾
            </span>
          </button>

          {/*
            Brana obaveznosti (vidi razred). Iz stabla pristupačnosti je van —
            gumb iznad već nosi ime i stanje — pa joj ime stoji kao podatak, za
            popis palih polja.
          */}
          <input
            /*
              `type="date"` nije ukras: `zakonForme` iz vrste izvodi REČENICU, pa
              prazno obavezno polje ovdje kaže "izaberite datum", a ne "polje je
              obavezno". Vrijednost je uvijek ISO iz kalendara, pa `badInput` —
              nalaz zbog kojeg zakon forme postoji — ovdje ne može nastati.
            */
            type="date"
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
            data-oznaka={label}
            data-fokus={id}
            name={name}
            required={obavezno}
            value={value}
            onChange={() => {
              /* Vrijednost mijenja kalendar; ovo polje je samo brana. */
            }}
          />

          {otvoren ? (
            <Kalendar
              ton={ton}
              vrijednost={value}
              mjesec={mjesec}
              naMjesec={postaviMjesec}
              min={min}
              max={max}
              naIzbor={(dan) => {
                onValue(dan)
                postaviOtvoren(false)
              }}
            />
          ) : null}

          {/*
            Prazno je legitiman odgovor svugdje gdje polje nije obavezno (filtar
            razdoblja, ciljno vrijeme "odmah"), pa izbor mora imati i izlaz.
          */}
          {!obavezno && value !== '' ? (
            <button
              type="button"
              data-ocisti="datum"
              className={`touch-target mt-1 text-xs underline ${razredPrigusen(ton)}`}
              onClick={() => {
                onValue('')
                postaviOtvoren(false)
              }}
            >
              {t('polje.ocisti')}
            </button>
          ) : null}
        </div>
      )}
    </OkvirTona>
  )
}
